import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { compressImage } from "../../firebase/upload";
import { FiUpload, FiCheck, FiPlus, FiTrash2 } from "react-icons/fi";

const defaultContent = {
  bannerImage: "/authorcorner/banner.jpg",
  introHeading: "Rosalita Spreads Kindness",
  introSubheading: "About the Author",
  introParagraph1: "Kristin Schmidt Larson is the author of Traveling Caravan: Growing Up Under the Big Top. Her story reflects family, resilience, and a lifelong commitment to kindness and advocacy. This page introduces her book, her mission, and the places where readers can find it.",
  introParagraph2: "The book is available for purchase through major retailers and select bookstores. It is also offered in e-book format in several countries. Below you will find links, store listings, and availability information.",
  bookListHeading: "Rosalita Spreads Kindness:",
  bookListItems: ["Message for kids & children", "The newest beginning", "Travel stories, memories, and growth", "Words to live by", "D.B. Books"],
  comingSoonText: "/ Coming Soon to Bookstores in South Dakota.",
  authorImage: "/authorcorner/author-book.jpg",
  platformHeading: "My children\u2019s book is now available for purchase on these platforms",
  platformImages: ["/authorcorner/platform-1.jpg", "/authorcorner/platform-2.jpg", "/authorcorner/platform-3.jpg", "/authorcorner/platform-4.jpg"],
  purchaseHeading: "Traveling Caravan",
  purchaseParagraph: "A memoir of a life lived under the big top, Traveling Caravan: Growing Up Under the Big Top shares the author\u2019s journey, family memories, and lessons learned from a unique childhood.",
  kindnessHeading: "Rosalita Spreads Kindness",
  kindnessParagraph: "The message behind the book is simple: kindness matters, stories matter, and sharing our experiences helps others feel seen and supported.",
  bookStoreHeading: "Bookstores",
  bookStoreDescription: "We\u2019re thrilled to announce that the first copy of our book is now available at select local stores!",
  ebookHeading: "E-book",
  ebookDescription: "Browse our collection of e-books available online.",
  subscribeHeading: "Get The Latest Updates",
  subscribePlaceholder: "Enter your email",
  subscribeButtonText: "Subscribe",
};

const AuthorCornerContent = () => {
  const [content, setContent] = useState(defaultContent);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "authorCornerContent"));
        if (snap.exists()) {
          const data = snap.data();
          setContent((prev) => ({
            ...prev, ...data,
            bookListItems: data.bookListItems || prev.bookListItems,
            platformImages: data.platformImages || prev.platformImages,
          }));
        }
      } catch (e) {}
      setLoading(false);
    };
    fetch();
  }, []);

  const handleUpload = async (e, field, maxW = 1200, quality = 0.5) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading((u) => ({ ...u, [field]: true }));
    try {
      const base64 = await compressImage(file, maxW, quality);
      setContent((s) => ({ ...s, [field]: base64 }));
      await setDoc(doc(db, "settings", "authorCornerContent"), { [field]: base64 }, { merge: true });
    } catch (e) { alert("Upload failed: " + e.message); }
    setUploading((u) => ({ ...u, [field]: false }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await setDoc(doc(db, "settings", "authorCornerContent"), content, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert("Save failed: " + e.message); }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm("Reset ALL author corner data to defaults?")) return;
    setSaving(true);
    try {
      await deleteDoc(doc(db, "settings", "authorCornerContent"));
      await setDoc(doc(db, "settings", "authorCornerContent"), defaultContent);
      setContent(defaultContent);
      alert("Reset successful!");
    } catch (e) { alert("Reset failed: " + e.message); }
    setSaving(false);
  };

  const updateArrayItem = (field, i, val) => {
    const arr = [...content[field]];
    arr[i] = val;
    setContent((s) => ({ ...s, [field]: arr }));
  };

  const addArrayItem = (field) => {
    setContent((s) => ({ ...s, [field]: [...s[field], ""] }));
  };

  const removeArrayItem = (field, i) => {
    setContent((s) => ({ ...s, [field]: s[field].filter((_, idx) => idx !== i) }));
  };

  const UploadField = ({ label, field, maxW = 1200, quality = 0.5 }) => (
    <div className="bg-gray-50 rounded-xl p-5">
      <label className="block text-[12px] font-bold text-gray-600 mb-3">{label}</label>
      {content[field] && <img src={content[field]} alt="" className="h-20 w-auto object-contain mb-3 bg-white rounded border p-1" />}
      {!content[field] && <div className="h-20 bg-gray-200 rounded mb-3 flex items-center justify-center text-[11px] text-gray-400 border-2 border-dashed">{label}</div>}
      <label className="flex items-center gap-2 cursor-pointer w-fit px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#e84fa5] text-[13px] text-gray-500 hover:text-[#e84fa5] transition">
        <FiUpload size={14} />
        {uploading[field] ? "Uploading..." : `Upload ${label}`}
        <input type="file" accept="image/*" onChange={(e) => handleUpload(e, field, maxW, quality)} className="hidden" disabled={uploading[field]} />
      </label>
    </div>
  );

  const ArrayInput = ({ field, label, placeholder = "Value" }) => (
    <div className="bg-gray-50 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <label className="block text-[12px] font-bold text-gray-600">{label}</label>
        <button type="button" onClick={() => addArrayItem(field)}
          className="flex items-center gap-1 text-[11px] text-[#e84fa5] font-semibold hover:text-pink-700 transition">
          <FiPlus size={12} /> Add
        </button>
      </div>
      <div className="space-y-2">
        {(content[field] || []).map((val, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={val} onChange={(e) => updateArrayItem(field, i, e.target.value)}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#e84fa5]" placeholder={placeholder} />
            <button type="button" onClick={() => removeArrayItem(field, i)} className="text-red-400 hover:text-red-600 shrink-0"><FiTrash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return <AdminLayout><div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="mb-6">
          <h1 className="text-2xl font-extrabold text-black">Author's Corner Content</h1>
          <p className="text-sm text-gray-400">Manage all content displayed on the Author's Corner page</p>
        </div>

        <form onSubmit={handleSave} className="space-y-8">
          {/* Banner */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Top Banner</h2>
            <UploadField label="Banner Image" field="bannerImage" maxW={1920} quality={0.6} />
          </div>

          {/* Intro Section */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Intro Section</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Heading</label>
                <input value={content.introHeading} onChange={(e) => setContent((s) => ({ ...s, introHeading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Subheading</label>
                <input value={content.introSubheading} onChange={(e) => setContent((s) => ({ ...s, introSubheading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Paragraph 1</label>
                <textarea value={content.introParagraph1} onChange={(e) => setContent((s) => ({ ...s, introParagraph1: e.target.value }))} rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-y" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Paragraph 2</label>
                <textarea value={content.introParagraph2} onChange={(e) => setContent((s) => ({ ...s, introParagraph2: e.target.value }))} rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-y" />
              </div>
            </div>

            <div className="mt-4">
              <ArrayInput field="bookListItems" label="Book Info List Items" placeholder="List item" />
            </div>

            <div className="mt-4">
              <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Coming Soon Text</label>
              <input value={content.comingSoonText} onChange={(e) => setContent((s) => ({ ...s, comingSoonText: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
            </div>

            <div className="mt-4">
              <UploadField label="Author Book Image" field="authorImage" maxW={600} quality={0.6} />
            </div>
          </div>

          {/* Platform Images */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Platform Images</h2>
            <div className="mb-4">
              <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Heading</label>
              <input value={content.platformHeading} onChange={(e) => setContent((s) => ({ ...s, platformHeading: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-4">
                  <label className="block text-[11px] font-bold text-gray-600 mb-2">Image {i + 1}</label>
                  {content.platformImages[i] ? (
                    <img src={content.platformImages[i]} alt="" className="h-16 w-full object-contain mb-2 bg-white rounded border p-1" />
                  ) : (
                    <div className="h-16 bg-gray-200 rounded mb-2 flex items-center justify-center text-[10px] text-gray-400 border-2 border-dashed">Image {i + 1}</div>
                  )}
                  <label className="flex items-center gap-1.5 cursor-pointer w-fit px-3 py-1.5 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#e84fa5] text-[11px] text-gray-500 hover:text-[#e84fa5] transition">
                    <FiUpload size={12} />
                    {uploading[`platformImage${i}`] ? "..." : "Upload"}
                    <input type="file" accept="image/*" onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setUploading((u) => ({ ...u, [`platformImage${i}`]: true }));
                      try {
                        const base64 = await compressImage(file, 1200, 0.5);
                        const arr = [...content.platformImages];
                        arr[i] = base64;
                        setContent((s) => ({ ...s, platformImages: arr }));
                        await setDoc(doc(db, "settings", "authorCornerContent"), { platformImages: arr }, { merge: true });
                      } catch (e) { alert("Upload failed: " + e.message); }
                      setUploading((u) => ({ ...u, [`platformImage${i}`]: false }));
                    }} className="hidden" disabled={uploading[`platformImage${i}`]} />
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Purchase Section */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Purchase / Text Section</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Purchase Heading</label>
                <input value={content.purchaseHeading} onChange={(e) => setContent((s) => ({ ...s, purchaseHeading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Kindness Heading</label>
                <input value={content.kindnessHeading} onChange={(e) => setContent((s) => ({ ...s, kindnessHeading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Purchase Paragraph</label>
                <textarea value={content.purchaseParagraph} onChange={(e) => setContent((s) => ({ ...s, purchaseParagraph: e.target.value }))} rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-y" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Kindness Paragraph</label>
                <textarea value={content.kindnessParagraph} onChange={(e) => setContent((s) => ({ ...s, kindnessParagraph: e.target.value }))} rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-y" />
              </div>
            </div>
          </div>

          {/* Book Store / E-Book Headings */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Book Store & E-Book Section</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Book Store Heading</label>
                <input value={content.bookStoreHeading} onChange={(e) => setContent((s) => ({ ...s, bookStoreHeading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">E-Book Heading</label>
                <input value={content.ebookHeading} onChange={(e) => setContent((s) => ({ ...s, ebookHeading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Book Store Description</label>
                <textarea value={content.bookStoreDescription} onChange={(e) => setContent((s) => ({ ...s, bookStoreDescription: e.target.value }))} rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-y" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">E-Book Description</label>
                <textarea value={content.ebookDescription} onChange={(e) => setContent((s) => ({ ...s, ebookDescription: e.target.value }))} rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-y" />
              </div>
            </div>
          </div>

          {/* Subscribe */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Subscribe Section</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Heading</label>
                <input value={content.subscribeHeading} onChange={(e) => setContent((s) => ({ ...s, subscribeHeading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Placeholder</label>
                <input value={content.subscribePlaceholder} onChange={(e) => setContent((s) => ({ ...s, subscribePlaceholder: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Button Text</label>
                <input value={content.subscribeButtonText} onChange={(e) => setContent((s) => ({ ...s, subscribeButtonText: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button type="submit" disabled={saving}
              className="px-8 py-3 bg-[#e84fa5] text-white font-bold text-[13px] uppercase rounded-lg hover:bg-pink-600 transition disabled:opacity-60">
              {saving ? "Saving..." : "Save Author's Corner"}
            </button>
            {saved && (
              <span className="flex items-center gap-2 text-green-600 text-[13px] font-semibold">
                <FiCheck /> Saved!
              </span>
            )}
            <button type="button" onClick={handleReset} disabled={saving}
              className="px-4 py-2 text-[11px] text-red-500 border border-red-300 rounded-lg hover:bg-red-50 transition font-semibold">
              Reset All Data
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AuthorCornerContent;
